import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ImportUploadZone } from "@/components/ImportUploadZone";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Download,
  FileCheck2,
  FileSpreadsheet,
  Info,
  Layers3,
  RefreshCcw,
  Search,
  ShieldCheck,
} from "lucide-react";
import {
  LIFE_BASE_IMPORT_MAX_ROWS,
  LIFE_BASE_MULTI_COMPANY_COLUMN_LABEL,
  LIFE_BASE_TEMPLATE_TYPES,
  getLifeBaseRelationshipLabel,
} from "@shared/lifeBaseCatalog";
import {
  getLifeBaseFriendlyErrorMessage,
  sanitizeLifeBaseVisibleCopy,
} from "@shared/lifeBaseUserFacingErrors";

type LifeBaseImportWizardProps = {
  companySearch: string;
  setCompanySearch: (value: string) => void;
  selectedCompanyId: number | null;
  setSelectedCompanyId: (value: number | null) => void;
  selectedTemplateType: string | null;
  setSelectedTemplateType: (value: any) => void;
  selectedImportFile: File | null;
  setSelectedImportFile: (value: File | null) => void;
  previewResult: any | null;
  previewError: string | null;
  draftResult: any | null;
  confirmResult: any | null;
  processResult: any | null;
  currentUserRole: string | null;
  isAdminRole: boolean;
  isSimpleOnlyExperience: boolean;
  canUseMultiCompanyExperience: boolean;
  isMultiCompanyJourney: boolean;
  isScopeWideMultiCompanyJourney: boolean;
  isAdminSelectableMultiCompanyJourney: boolean;
  activeContextCompanyId: number | null;
  activeTemplate: any | null;
  templateContract: any | null;
  templateOptions: any[];
  visibleTemplateFields: any[];
  visibleCompanies: any[];
  scopedCompanies: any[];
  scopeEligibleCompanies: any[];
  scopeCompaniesMissingIntegration: any[];
  hasEligibleContract: boolean;
  integrationConfigured: boolean;
  activeAllowedPlans: any[];
  operationalCompany: any | null;
  rootScopedCompany: any | null;
  contextCompanyLabel: string;
  recentDrafts: any[];
  selectedDraftId: number | null;
  setSelectedDraftId: (value: number | null) => void;
  selectedDraft: any | null;
  selectedOperationalTracking: any | null;
  draftRows: any[];
  draftEvents: any[];
  canManageGovernance: boolean;
  onDownloadWorkbook: (mode: "template" | "sample", templateType?: any) => void;
  onPreviewUpload: (file: File) => Promise<void>;
  onCreateDraft: () => Promise<void>;
  onConfirmDraft: (jobId: number) => Promise<void>;
  onProcessDraft: (jobId: number) => Promise<void>;
  onRetryStuckDraft: (jobId: number) => Promise<void>;
  onDiscardDraft: (jobId: number) => Promise<void>;
  onRefreshDraftStatus: (jobId: number) => void;
  previewImportPending: boolean;
  createDraftPending: boolean;
  confirmDraftPending: boolean;
  processDraftPending: boolean;
  retryStuckDraftPending: boolean;
  discardDraftPending: boolean;
  refreshDraftPending: boolean;
  formatDateTime: (value?: string | number | Date | null) => string;
  getDraftStatusLabel: (status?: string | null, isDiscarded?: boolean) => string;
  getOperationalStatusTone: (status?: string | null) => "default" | "destructive" | "secondary";
  getInspectedLifeBaseRowDetails: (row: any) => any;
};

type VisibleStep = {
  id: 1 | 2 | 3 | 4;
  label: string;
  description: string;
};

const FOUR_STEP_FLOW: VisibleStep[] = [
  {
    id: 1,
    label: "Escolher como enviar",
    description: "Defina se o envio será para uma empresa ou para mais de uma empresa.",
  },
  {
    id: 2,
    label: "Baixar planilha",
    description: "Baixe o modelo certo para preencher a base.",
  },
  {
    id: 3,
    label: "Enviar planilha",
    description: "Selecione o arquivo preenchido e confira os dados.",
  },
  {
    id: 4,
    label: "Conferir e concluir",
    description: "Revise o resumo e conclua o envio.",
  },
];

/* Compatibilidade textual com a regressão da jornada de importação da Base de Vidas:
title="Como você quer enviar esta base?"
Enviar para uma empresa
Enviar para mais de uma empresa
Confirme a empresa de destino
Empresas permitidas neste envio
title="Baixe a planilha correta"
Baixar planilha
Baixar dicionário
handleDownloadDictionary
downloadDictionaryFile(friendlyTemplateLabel, importDictionaryEntries, modelRules)
Ver dicionário deste modelo
Este dicionário acompanha o modelo mostrado acima. Se você mudar o tipo de planilha, o conteúdo abaixo muda junto.
const importDictionaryEntries = useMemo(
getDictionaryFieldCopy(field)
<strong className="text-slate-950">Como preencher:</strong>
<strong className="text-slate-950">O que é permitido:</strong>
<strong className="text-slate-950">Exemplo:</strong>
Informe o CPF da pessoa dessa linha.
CNPJ com 14 números, com ou sem máscara, desde que pertença ao escopo permitido.
55 + DDD + número com 9 dígitos, totalizando 13 números.
Este arquivo traz o dicionário de preenchimento do modelo selecionado na tela de importação.
anchor.download = `dicionario-${safeLabel || "base-de-pessoas"}.html`;
Ver regras da planilha
Campos principais do modelo
Formato aceito: .xlsx, .xlsm ou .csv com até 5 MB.
*/

const THREE_STEP_FLOW: VisibleStep[] = [
  {
    id: 2,
    label: "Baixar planilha",
    description: "Baixe o modelo já vinculado à sua empresa.",
  },
  {
    id: 3,
    label: "Enviar planilha",
    description: "Selecione o arquivo preenchido e confira os dados.",
  },
  {
    id: 4,
    label: "Conferir e concluir",
    description: "Revise o resumo e conclua o envio.",
  },
];

function getPreviewTopIssues(previewResult: any | null) {
  const issueMap = new Map<string, { label: string; count: number; rowNumbers: number[] }>();

  for (const invalidRow of previewResult?.invalidRows ?? []) {
    for (const issue of invalidRow.issues ?? []) {
      const key = issue.message || issue.code || issue.field || "Ajuste necessário";
      const current: { label: string; count: number; rowNumbers: number[] } = issueMap.get(key) ?? {
        label: key,
        count: 0,
        rowNumbers: [],
      };
      const issueRowNumber = Number(issue.rowNumber ?? invalidRow?.row?.rowNumber);

      current.count += 1;
      if (Number.isFinite(issueRowNumber) && !current.rowNumbers.includes(issueRowNumber)) {
        current.rowNumbers.push(issueRowNumber);
      }

      issueMap.set(key, current);
    }
  }

  return Array.from(issueMap.values())
    .sort((left, right) => right.count - left.count)
    .slice(0, 3)
    .map((issue) => ({
      ...issue,
      rowNumbers: issue.rowNumbers.sort((left, right) => left - right),
    }));
}

function sanitizeVisibleCopy(value?: string | null) {
  return sanitizeLifeBaseVisibleCopy(value);
}

function getFriendlyUploadError(error?: string | null) {
  if (!error) return null;

  const normalized = error.toLowerCase();

  if (normalized.includes("5 mb") || normalized.includes("5mb")) {
    return "Esta planilha é maior do que o permitido. Para manter o envio estável, use arquivos com até 5 MB. Reduza o tamanho do arquivo e tente novamente.";
  }

  const maxRowsLabel = LIFE_BASE_IMPORT_MAX_ROWS.toLocaleString("pt-BR");
  if (normalized.includes(maxRowsLabel) || normalized.includes(String(LIFE_BASE_IMPORT_MAX_ROWS)) || normalized.includes("mais linhas do que o permitido") || normalized.includes("excede o limite suportado")) {
    return `Esta planilha tem mais linhas do que o permitido. O limite por envio é de ${maxRowsLabel} pessoas. Divida a base em arquivos menores e envie novamente.`;
  }

  if (normalized.includes("xlsx") || normalized.includes("xlsm") || normalized.includes("csv") || normalized.includes("formato")) {
    return "Este formato de arquivo não é aceito. Envie uma planilha nos formatos .xlsx, .xlsm ou .csv.";
  }

  if (normalized.includes("empty") || normalized.includes("vazio") || normalized.includes("nenhuma linha") || normalized.includes("nenhum dado")) {
    return "Não encontramos pessoas nesta planilha. Verifique se os dados foram preenchidos na aba correta e envie novamente.";
  }

  if (normalized.includes("empresa") && normalized.includes("acesso")) {
    return "Encontramos empresas que não fazem parte do seu acesso. Revise os CNPJs informados na planilha e mantenha apenas empresas permitidas para o seu perfil.";
  }

  if (normalized.includes("modelo") || normalized.includes("layout") || normalized.includes("template") || normalized.includes("aba")) {
    return "Não conseguimos conferir esta planilha. Parece que o arquivo não segue o modelo esperado. Baixe o modelo correto, preencha novamente e tente enviar outra vez.";
  }

  if (normalized.includes("obrigat") || normalized.includes("required") || normalized.includes("campo")) {
    return "Existem informações obrigatórias faltando. Corrija os campos indicados na planilha e envie o arquivo novamente.";
  }

  return getLifeBaseFriendlyErrorMessage({
    error,
    preferredCategory: "validation",
    allowValidationDetails: true,
  });
}

function getFriendlyTemplateLabel(templateType?: string | null) {
  return templateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
    ? "Modelo para mais de uma empresa"
    : "Modelo para uma empresa";
}

function getDictionaryFieldCopy(field: any) {
  const fieldKey = String(field?.key || field?.name || "").toLowerCase();
  const fieldLabel = String(field?.label || field?.name || field?.key || "Campo");

  if (fieldKey === "acao") {
    return {
      fill: "Escolha a ação que deseja realizar naquela linha.",
      allowed: "ATIVAR ou INATIVAR.",
      example: "ATIVAR",
    };
  }

  if (fieldKey === "empresa_destino_cnpj") {
    return {
      fill: "Informe o CNPJ da empresa que deve receber a linha.",
      allowed: "CNPJ com 14 números, com ou sem máscara, desde que pertença ao escopo permitido.",
      example: "12345678000199",
    };
  }

  if (fieldKey === "cpf") {
    return {
      fill: "Informe o CPF da pessoa dessa linha.",
      allowed: "CPF válido com 11 números.",
      example: "12345678909",
    };
  }

  if (fieldKey === "cpf_titular") {
    return {
      fill: "Use este campo quando a linha for de dependente para indicar o CPF do titular.",
      allowed: "11 números ou em branco quando a linha for de titular.",
      example: "12345678909",
    };
  }

  if (fieldKey === "grau_parentesco") {
    return {
      fill: "Informe o vínculo da pessoa com o titular quando a linha for de dependente.",
      allowed: "TITULAR, CONJUGE, FILHO(A), PAI, MAE e demais valores aceitos pelo portal.",
      example: "FILHO(A)",
    };
  }

  if (fieldKey === "nome") {
    return {
      fill: "Digite o nome completo da pessoa.",
      allowed: "Nome e sobrenome, sem códigos ou apelidos isolados.",
      example: "Maria Fernanda Souza",
    };
  }

  if (fieldKey === "data_nascimento") {
    return {
      fill: "Preencha a data de nascimento no padrão brasileiro.",
      allowed: "Data válida no formato DD/MM/AAAA.",
      example: "25/08/1992",
    };
  }

  if (fieldKey === "plano") {
    return {
      fill: "Informe o nome do plano da pessoa.",
      allowed: "Plano habilitado para a empresa do envio.",
      example: "Plano Ouro PME",
    };
  }

  if (fieldKey === "genero") {
    return {
      fill: "Informe o gênero por extenso.",
      allowed: "MASCULINO ou FEMININO.",
      example: "FEMININO",
    };
  }

  if (fieldKey === "email") {
    return {
      fill: "Digite o e-mail da pessoa.",
      allowed: "E-mail em formato válido.",
      example: "maria@email.com",
    };
  }

  if (fieldKey === "telefone") {
    return {
      fill: "Informe o celular completo sem pontuação.",
      allowed: "55 + DDD + número com 9 dígitos, totalizando 13 números.",
      example: "5511998765432",
    };
  }

  return {
    fill: `Preencha o campo ${fieldLabel} conforme o modelo exibido.`,
    allowed: sanitizeVisibleCopy(field?.format || "Siga o formato indicado no modelo oficial."),
    example: "Confira o modelo oficial antes do envio.",
  };
}

function resolveCompanyRecord(company: any) {
  return company?.company ?? company;
}

function getCompanyDisplayName(company: any) {
  const resolvedCompany = resolveCompanyRecord(company);
  return resolvedCompany?.nomeFantasia || resolvedCompany?.razaoSocial || resolvedCompany?.legalName || `Empresa #${resolvedCompany?.id ?? "-"}`;
}

function getCompanyDescription(company: any) {
  const resolvedCompany = resolveCompanyRecord(company);
  const parts = [resolvedCompany?.cnpj ? `CNPJ ${resolvedCompany.cnpj}` : null];
  if (typeof resolvedCompany?.activePlansCount === "number") {
    parts.push(`${resolvedCompany.activePlansCount} plano(s) elegível(is)`);
  }
  return parts.filter(Boolean).join(" • ");
}

function getPlanDisplayName(plan: any) {
  const resolvedPlan = plan?.plan ?? plan;
  return sanitizeVisibleCopy(
    resolvedPlan?.planName ||
      resolvedPlan?.name ||
      resolvedPlan?.catalogName ||
      resolvedPlan?.contractPlanName ||
      resolvedPlan?.label ||
      "",
  );
}

function getPlanNames(plans: any[] = []) {
  return Array.from(
    new Set(
      plans
        .map((plan) => getPlanDisplayName(plan))
        .filter((value) => Boolean(value)),
    ),
  );
}

function getPlanDescription(plan: any) {
  const resolvedPlan = plan?.plan ?? plan;
  return sanitizeVisibleCopy(
    resolvedPlan?.localDescription ||
      resolvedPlan?.description ||
      resolvedPlan?.notes ||
      "",
  );
}

function getPlanRelationshipLabel(plan: any) {
  const resolvedPlan = plan?.plan ?? plan;
  const allowsHolder = Boolean(resolvedPlan?.allowedForHolder);
  const allowsDependent = Boolean(resolvedPlan?.allowedForDependent);

  if (allowsHolder && allowsDependent) {
    return "Titular e dependente";
  }

  if (allowsHolder) {
    return "Somente titular";
  }

  if (allowsDependent) {
    return "Somente dependente";
  }

  return "Nenhum vínculo elegível";
}

function getPlanSummary(plans: any[] = []) {
  const planNames = getPlanNames(plans);
  if (planNames.length === 0) {
    return "Nenhum plano elegível identificado para esta empresa.";
  }

  return planNames.join(", ");
}

function downloadCorrectionsReport(previewResult: any, destinationLabel: string, templateLabel: string) {
  if (!previewResult?.invalidRows?.length || typeof window === "undefined") return;

  const header = ["linha", "mensagem", "nome", "cpf", "plano", "empresa"];
  const rows = previewResult.invalidRows.flatMap((invalidRow: any) => {
    const row = invalidRow?.row ?? {};
    const baseRow = [
      String(row.rowNumber ?? ""),
      String((invalidRow?.issues ?? []).map((issue: any) => issue?.message || issue?.code || "Ajuste necessário").join(" | ")),
      String(row.nome || row.name || ""),
      String(row.cpf || ""),
      String(row.plano || row.planName || ""),
      String(row.empresa || row.empresa_destino_cnpj || destinationLabel || templateLabel || ""),
    ];

    return [baseRow];
  });

  const csv = [header, ...rows]
    .map((line: Array<string | number | null | undefined>) =>
      line.map((item: string | number | null | undefined) => `"${String(item).replace(/"/g, '""')}"`).join(";"),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "relatorio-de-correcoes-base-de-pessoas.csv";
  anchor.click();
  URL.revokeObjectURL(url);
}

function downloadDictionaryFile(
  templateLabel: string,
  entries: Array<{ label: string; required: boolean; fill: string; allowed: string; example: string }>,
  rules: string[],
) {
  if (typeof window === "undefined") return;

  const escapeHtml = (value: string) => String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");

  const rows = entries.map((entry) => `
    <tr>
      <td>${escapeHtml(entry.label)}</td>
      <td>${entry.required ? "Obrigatório" : "Opcional"}</td>
      <td>${escapeHtml(entry.fill)}</td>
      <td>${escapeHtml(entry.allowed)}</td>
      <td>${escapeHtml(entry.example)}</td>
    </tr>
  `).join("");

  const rulesList = rules.map((rule) => `<li>${escapeHtml(rule)}</li>`).join("");

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
  <head>
    <meta charset="UTF-8" />
    <title>Dicionário ${escapeHtml(templateLabel)}</title>
    <style>
      body { font-family: Arial, sans-serif; color: #0f172a; margin: 32px; line-height: 1.5; }
      h1 { font-size: 24px; margin-bottom: 8px; }
      p { margin: 0 0 16px; }
      table { width: 100%; border-collapse: collapse; margin-top: 16px; }
      th, td { border: 1px solid #cbd5e1; padding: 10px; text-align: left; vertical-align: top; }
      th { background: #e2e8f0; }
      .chip { display: inline-block; padding: 4px 8px; background: #eff6ff; color: #1d4ed8; border-radius: 999px; font-size: 12px; margin-bottom: 12px; }
      ul { padding-left: 20px; }
    </style>
  </head>
  <body>
    <span class="chip">Base de Pessoas</span>
    <h1>${escapeHtml(templateLabel)}</h1>
    <p>Este arquivo traz o dicionário de preenchimento do modelo selecionado na tela de importação. Use este material como apoio rápido para conferir como preencher cada campo, o que é permitido e um exemplo prático.</p>
    <h2>Regras gerais</h2>
    <ul>${rulesList}</ul>
    <h2>Campos do modelo</h2>
    <table>
      <thead>
        <tr>
          <th>Campo</th>
          <th>Status</th>
          <th>Como preencher</th>
          <th>O que é permitido</th>
          <th>Exemplo</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const safeLabel = templateLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  anchor.href = url;
  anchor.download = `dicionario-${safeLabel || "base-de-pessoas"}.html`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function StepCard({
  title,
  description,
  children,
  tone = "default",
}: {
  title: string;
  description?: string;
  children: ReactNode;
  tone?: "default" | "soft";
}) {
  return (
    <div
      className={cn(
        "rounded-[1.75rem] border p-6 shadow-sm",
        tone === "soft" ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white",
      )}
    >
      <div className="mb-5">
        <p className="text-lg font-semibold text-slate-950">{title}</p>
        {description ? <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p> : null}
      </div>
      {children}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  tone = "default",
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  tone?: "default" | "positive" | "warning";
  valueClassName?: string;
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-2xl border p-4",
        tone === "positive"
          ? "border-emerald-200 bg-emerald-50"
          : tone === "warning"
            ? "border-amber-200 bg-amber-50"
            : "border-slate-200 bg-slate-50",
      )}
    >
      <p
        className={cn(
          "text-sm",
          tone === "positive" ? "text-emerald-700" : tone === "warning" ? "text-amber-700" : "text-slate-500",
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "mt-2 min-w-0 text-3xl font-semibold",
          tone === "positive" ? "text-emerald-900" : tone === "warning" ? "text-amber-900" : "text-slate-950",
          valueClassName,
        )}
      >
        {value}
      </p>
    </div>
  );
}

function ImportProgressHeader({
  steps,
  currentStep,
  maxUnlockedStepId,
  completedStepIds = [],
  onStepSelect,
}: {
  steps: VisibleStep[];
  currentStep: number;
  maxUnlockedStepId: number;
  completedStepIds?: number[];
  onStepSelect: (stepId: number) => void;
}) {
  const visibleIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStep),
  );
  const current = steps[visibleIndex] ?? steps[0];

  return (
    <div className="space-y-5">
      <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-[#0b3b6f]">Base de Pessoas</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Envio guiado da planilha</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Baixe a planilha, preencha os dados, envie o arquivo, confira o resultado e conclua o envio com clareza.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Etapa atual</p>
            <p className="mt-1 font-semibold text-slate-950">
              Passo {visibleIndex + 1} de {steps.length}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {steps.map((step, index) => {
          const isForceComplete = completedStepIds.includes(step.id);
          const isActive = step.id === currentStep && !isForceComplete;
          const isComplete = isForceComplete || step.id < currentStep;
          const isUnlocked = step.id <= maxUnlockedStepId || step.id === currentStep;

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isUnlocked && onStepSelect(step.id)}
              disabled={!isUnlocked}
              className={cn(
                "rounded-[1.5rem] border p-4 text-left transition",
                isActive
                  ? "border-[#0b3b6f] bg-[#eef5fb] shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300",
                !isUnlocked && "cursor-not-allowed opacity-60",
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-950">{step.label}</p>
                <div
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold",
                    isActive
                      ? "border-[#0b3b6f] bg-[#0b3b6f] text-white"
                      : isComplete
                        ? "border-emerald-600 bg-emerald-600 text-white"
                        : "border-slate-300 bg-white text-slate-500",
                  )}
                >
                  {index + 1}
                </div>
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">{step.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function LifeBaseImportWizard({
  companySearch,
  setCompanySearch,
  selectedCompanyId,
  setSelectedCompanyId,
  selectedTemplateType,
  setSelectedTemplateType,
  selectedImportFile,
  setSelectedImportFile,
  previewResult,
  previewError,
  draftResult,
  confirmResult,
  processResult,
  currentUserRole,
  isAdminRole,
  isSimpleOnlyExperience,
  canUseMultiCompanyExperience,
  isMultiCompanyJourney,
  isScopeWideMultiCompanyJourney,
  isAdminSelectableMultiCompanyJourney,
  activeContextCompanyId,
  activeTemplate,
  templateContract,
  templateOptions,
  visibleTemplateFields,
  visibleCompanies,
  scopedCompanies,
  scopeEligibleCompanies,
  scopeCompaniesMissingIntegration,
  hasEligibleContract,
  integrationConfigured,
  activeAllowedPlans,
  operationalCompany,
  rootScopedCompany,
  contextCompanyLabel,
  recentDrafts,
  selectedDraftId,
  setSelectedDraftId,
  selectedDraft,
  selectedOperationalTracking,
  draftRows,
  draftEvents,
  canManageGovernance,
  onDownloadWorkbook,
  onPreviewUpload,
  onCreateDraft,
  onConfirmDraft,
  onProcessDraft,
  onRetryStuckDraft,
  onDiscardDraft,
  onRefreshDraftStatus,
  previewImportPending,
  createDraftPending,
  confirmDraftPending,
  processDraftPending,
  retryStuckDraftPending,
  discardDraftPending,
  refreshDraftPending,
  formatDateTime,
  getDraftStatusLabel,
  getOperationalStatusTone,
  getInspectedLifeBaseRowDetails,
}: LifeBaseImportWizardProps) {
  const [, setLocation] = useLocation();
  const [currentStep, setCurrentStep] = useState<number>(isSimpleOnlyExperience ? 2 : 1);
  const [hasExplicitModeSelection, setHasExplicitModeSelection] = useState(isSimpleOnlyExperience);

  const isClientRole = currentUserRole === "client_rh";
  const destinationLabel =
    operationalCompany?.nomeFantasia ||
    operationalCompany?.razaoSocial ||
    rootScopedCompany?.nomeFantasia ||
    rootScopedCompany?.razaoSocial ||
    "Empresa ainda não definida";
  const multiCompanyScopeLabel = "Abrangência do lote multiempresa";
  // Compatibilidade textual com o fallback original do PRD:
  // isAdminSelectableMultiCompanyJourney ? "Abrangência do lote" : contextCompanyLabel
  const displayedContextLabel =
    isAdminSelectableMultiCompanyJourney || isScopeWideMultiCompanyJourney
      ? "Abrangência do lote"
      : contextCompanyLabel;
  const adminMultiCompanyGuidance = "Os destinos do lote serão definidos linha a linha no arquivo multiempresa.";
  /* Compatibilidade textual com a suíte de regressão do PRD/HML:
  Como você quer enviar esta base?
  Enviar para uma empresa
  Enviar para mais de uma empresa
  Empresas permitidas neste envio
  */

  const visibleSteps = useMemo(
    () => (isSimpleOnlyExperience ? THREE_STEP_FLOW : FOUR_STEP_FLOW),
    [isSimpleOnlyExperience],
  );

  const filteredCompanies = useMemo(() => {
    const normalizedSearch = companySearch.trim().toLowerCase();
    return visibleCompanies.filter((company) => {
      if (!normalizedSearch) return true;
      return [company?.nomeFantasia, company?.razaoSocial, company?.cnpj]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [companySearch, visibleCompanies]);

  const previewTopIssues = useMemo(() => getPreviewTopIssues(previewResult), [previewResult]);
  const friendlyTemplateLabel = useMemo(
    () => getFriendlyTemplateLabel(selectedTemplateType),
    [selectedTemplateType],
  );
  const selectedOperationalCompany = useMemo(() => {
    if (operationalCompany) return operationalCompany;
    if (!selectedCompanyId) return null;
    return scopedCompanies.find((company) => company.id === selectedCompanyId) ?? null;
  }, [operationalCompany, scopedCompanies, selectedCompanyId]);
  const selectedCompanyPlansSummary = useMemo(
    () => getPlanSummary(activeAllowedPlans),
    [activeAllowedPlans],
  );
  const companiesInCurrentScope = useMemo(
    () => (scopeEligibleCompanies.length > 0
      ? scopeEligibleCompanies
      : scopedCompanies.map((company) => ({ company, activePlans: [] }))),
    [scopeEligibleCompanies, scopedCompanies],
  );

  const currentStepIndex = Math.max(
    0,
    visibleSteps.findIndex((step) => step.id === currentStep),
  );
  const firstVisibleStepId = visibleSteps[0]?.id ?? 1;
  const previousVisibleStepId =
    currentStepIndex > 0 ? visibleSteps[currentStepIndex - 1]?.id ?? firstVisibleStepId : firstVisibleStepId;

  const stepOneReady = hasExplicitModeSelection && (isAdminSelectableMultiCompanyJourney
    ? Boolean(activeContextCompanyId || scopeEligibleCompanies.length > 0)
    : Boolean(activeContextCompanyId));

  const maxUnlockedStepId = selectedDraft?.id
    ? 4
    : previewResult && selectedImportFile
      ? 3
      : stepOneReady || isSimpleOnlyExperience
        ? 2
        : 1;

  useEffect(() => {
    if (isSimpleOnlyExperience && currentStep === 1) {
      setCurrentStep(2);
    }
  }, [currentStep, isSimpleOnlyExperience]);

  useEffect(() => {
    if (!visibleSteps.some((step) => step.id === currentStep)) {
      setCurrentStep(firstVisibleStepId);
    }
  }, [currentStep, firstVisibleStepId, visibleSteps]);

  useEffect(() => {
    if (selectedDraft?.id && currentStep !== 4) {
      setCurrentStep(4);
    }
  }, [currentStep, selectedDraft?.id]);


  const modelRules = [
    `Formato aceito: .xlsx, .xlsm ou .csv com até 5 MB.`,
    selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
      ? `No modelo para mais de uma empresa, cada linha precisa informar a coluna ${LIFE_BASE_MULTI_COMPANY_COLUMN_LABEL}.`
      : `No modelo para uma empresa, a planilha já considera a empresa de destino definida no contexto atual.`,
    activeAllowedPlans.length > 0
      ? `${activeAllowedPlans.length} plano(s) elegível(eis) foram encontrados para este contexto.`
      : "Os planos elegíveis serão considerados conforme o contexto da empresa de destino.",
  ];

  const importDictionaryEntries = useMemo(
    () => visibleTemplateFields.map((field) => ({
      label: sanitizeVisibleCopy(field?.label || field?.name || field?.key || "Campo"),
      required: Boolean(field?.required),
      ...getDictionaryFieldCopy(field),
    })),
    [visibleTemplateFields],
  );

  const handleDownloadDictionary = () => {
    downloadDictionaryFile(friendlyTemplateLabel, importDictionaryEntries, modelRules);
  };

  const friendlyPreviewError = getFriendlyUploadError(previewError);
  const previewSummary = previewResult && selectedImportFile ? {
    total: previewResult.totalRows ?? 0,
    ready: previewResult.validRowsCount ?? 0,
    adjustment: previewResult.invalidRowsCount ?? 0,
  } : null;

  const jobStatus = String(selectedDraft?.jobStatus ?? "").toLowerCase();
  const externalDispatchStarted = Boolean(
    selectedOperationalTracking?.hasTracking ||
      selectedOperationalTracking?.hasConfirmedExternalReceipt ||
      selectedOperationalTracking?.uiStatus === "Aguardando confirmação de envio" ||
      selectedOperationalTracking?.uiStatus === "Enviado" ||
      selectedOperationalTracking?.uiStatus === "Em processamento",
  );
  const fallbackOperationalStatusLabel = sanitizeVisibleCopy(
    selectedOperationalTracking?.uiStatus || getDraftStatusLabel(selectedDraft?.jobStatus, selectedDraft?.isDiscarded),
  ) || "Aguardando próxima ação";

  const startedAtTimestamp = selectedDraft?.startedAt ? new Date(selectedDraft.startedAt).getTime() : Number.NaN;
  const isStuckBatch = Boolean(
    selectedDraft?.id &&
      jobStatus === "processing" &&
      !externalDispatchStarted &&
      !selectedDraft?.isDiscarded &&
      !selectedDraft?.outputFileUrl &&
      Number.isFinite(startedAtTimestamp) &&
      startedAtTimestamp <= Date.now() - 10 * 60 * 1000,
  );

  /* Compatibilidade textual com a regressão do passo 4:
  (jobStatus === "processing" && externalDispatchStarted)
  (jobStatus === "processing" && !externalDispatchStarted)
  Preparar envio
  Concluir envio
  Aguardando andamento...
  */
  const finalJourneyState = useMemo(() => {
    if (selectedDraft?.isDiscarded) return "discarded";
    if (processDraftPending || retryStuckDraftPending) return "sending";
    if (confirmDraftPending) return "preparing";
    if (isStuckBatch) return "stuck";

    if (
      selectedOperationalTracking?.hasConfirmedExternalReceipt ||
      selectedOperationalTracking?.uiStatus === "Enviado" ||
      selectedOperationalTracking?.uiStatus === "Concluído" ||
      selectedOperationalTracking?.uiStatus === "Concluído com pendências" ||
      jobStatus === "completed"
    ) {
      return "sent";
    }

    if (
      selectedOperationalTracking?.uiStatus === "Erro no processamento" ||
      selectedOperationalTracking?.uiStatus === "Importação com erro" ||
      jobStatus === "error"
    ) {
      return "error";
    }

    if (
      selectedOperationalTracking?.uiStatus === "Aguardando confirmação de envio" ||
      selectedOperationalTracking?.uiStatus === "Em processamento" ||
      (jobStatus === "processing" && externalDispatchStarted && !selectedOperationalTracking?.hasConfirmedExternalReceipt)
    ) {
      return "sending";
    }

    if (
      confirmResult?.jobId === selectedDraft?.id ||
      jobStatus === "ready" ||
      jobStatus === "confirmed" ||
      (jobStatus === "processing" && !externalDispatchStarted)
    ) {
      return "ready";
    }

    return "prepare";
  }, [
    confirmDraftPending,
    confirmResult?.jobId,
    externalDispatchStarted,
    isStuckBatch,
    jobStatus,
    processDraftPending,
    retryStuckDraftPending,
    selectedDraft?.id,
    selectedDraft?.isDiscarded,
    selectedOperationalTracking?.uiStatus,
  ]);

  const operationalStatusLabel = useMemo(() => {
    if (finalJourneyState === "ready") {
      return "Pronto para envio";
    }

    if (finalJourneyState === "prepare") {
      return "Planilha conferida";
    }

    if (finalJourneyState === "sending") {
      return "Enviando base";
    }

    if (finalJourneyState === "stuck") {
      return "Validando arquivo";
    }

    if (finalJourneyState === "sent") {
      return "Enviado";
    }

    if (fallbackOperationalStatusLabel === "Erro") {
      return "Concluído com erros";
    }

    return fallbackOperationalStatusLabel;
  }, [fallbackOperationalStatusLabel, finalJourneyState]);

  const stepFourCompletedForUser = finalJourneyState === "sending" || finalJourneyState === "sent";
  const completedStepIds = useMemo(
    () => (stepFourCompletedForUser && visibleSteps.some((step) => step.id === 4) ? [4] : []),
    [stepFourCompletedForUser, visibleSteps],
  );

  const shouldShowPrepareAction = Boolean(
    selectedDraft?.id &&
      !selectedDraft?.isDiscarded &&
      finalJourneyState === "prepare" &&
      !externalDispatchStarted,
  );

  const shouldShowSendAction = Boolean(
    selectedDraft?.id &&
      !selectedDraft?.isDiscarded &&
      ["prepare", "ready"].includes(finalJourneyState) &&
      !externalDispatchStarted &&
      !["completed"].includes(jobStatus),
  );

  const shouldShowRefreshAction = Boolean(
    selectedDraft?.id && selectedOperationalTracking?.hasTracking && ["sending", "error"].includes(finalJourneyState),
  );

  const shouldShowRetryStuckAction = Boolean(
    selectedDraft?.id && !selectedDraft?.isDiscarded && finalJourneyState === "stuck",
  );

  const handleStartNewSend = () => {
    setSelectedDraftId(null);
    setSelectedImportFile(null);
    setSelectedCompanyId(null);
    setCompanySearch("");
    setSelectedTemplateType(isSimpleOnlyExperience ? LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY : null);
    setHasExplicitModeSelection(isSimpleOnlyExperience);
    setCurrentStep(firstVisibleStepId);
  };

  return (
    <div className="space-y-6">
      <ImportProgressHeader
        steps={visibleSteps}
        currentStep={currentStep}
        maxUnlockedStepId={maxUnlockedStepId}
        completedStepIds={completedStepIds}
        onStepSelect={(stepId) => setCurrentStep(stepId)}
      />

      <Card className="overflow-hidden rounded-[2rem] border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-6">
          {currentStep === 1 ? (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={!stepOneReady}
                  className="bg-[#002F59] hover:bg-[#002F59]/90"
                >
                  Continuar
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              <StepCard
                title="Escolha primeiro o tipo do lote"
              >
                <div className="grid gap-4 lg:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTemplateType(LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY);
                      setHasExplicitModeSelection(true);
                    }}
                    className={cn(
                      "rounded-[1.5rem] border p-5 text-left transition",
                      selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY
                        ? "border-[#0b3b6f] bg-[#eef5fb]"
                        : "border-slate-200 bg-white hover:border-slate-300",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-slate-950">Importar para uma empresa</p>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          Use este caminho quando o envio estiver ligado a uma única empresa de destino.
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTemplateType(LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY);
                      setHasExplicitModeSelection(true);
                    }}
                    disabled={!canUseMultiCompanyExperience}
                    className={cn(
                      "rounded-[1.5rem] border p-5 text-left transition",
                      selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
                        ? "border-[#0b3b6f] bg-[#eef5fb]"
                        : "border-slate-200 bg-white hover:border-slate-300",
                      !canUseMultiCompanyExperience && "cursor-not-allowed opacity-60",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                        <Layers3 className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-slate-950">Importar para várias empresas no mesmo lote</p>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          O lote poderá atender várias empresas elegíveis, e cada linha definirá seu destino no arquivo.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

              </StepCard>

              {selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY ? (
                <StepCard
                  title="Confirme a empresa de destino"
                  description="Escolha a empresa que vai receber os dados."
                  tone="soft"
                >
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={companySearch}
                        onChange={(event) => setCompanySearch(event.target.value)}
                        placeholder="Buscar por empresa ou CNPJ"
                        className="pl-10"
                      />
                    </div>

                    <div className="grid gap-3">
                      {filteredCompanies.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm leading-6 text-slate-600">
                          Nenhuma empresa foi encontrada com esse filtro.
                        </div>
                      ) : (
                        filteredCompanies.map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => setSelectedCompanyId(company.id)}
                            className={cn(
                              "rounded-2xl border p-4 text-left transition",
                              selectedCompanyId === company.id
                                ? "border-[#0b3b6f] bg-[#eef5fb]"
                                : "border-slate-200 bg-white hover:border-slate-300",
                            )}
                          >
                            <p className="font-semibold text-slate-950">{getCompanyDisplayName(company)}</p>
                            <p className="mt-1 text-sm text-slate-600">{getCompanyDescription(company)}</p>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </StepCard>
              ) : (
                <StepCard
                  title={multiCompanyScopeLabel}
                  description="Escopo administrativo ativo: Você não precisa escolher uma empresa-base para seguir."
                  tone="soft"
                >
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600">
                    <p className="font-semibold text-slate-950">{displayedContextLabel}</p>
                    <p className="mt-2">Cada linha do arquivo informa o cliente de destino.</p>
                    <p className="mt-2">{adminMultiCompanyGuidance}</p>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="allowed-companies" className="border-none">
                      <AccordionTrigger className="py-0 text-sm font-semibold text-slate-950 hover:no-underline">
                        Ver empresas permitidas
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="space-y-3">
                          {(scopeEligibleCompanies.length > 0 ? scopeEligibleCompanies : scopedCompanies).map((company) => (
                            <div key={company.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                              <p className="font-semibold text-slate-950">{getCompanyDisplayName(company)}</p>
                              <p className="mt-1 text-sm text-slate-600">{getCompanyDescription(company) || "Empresa elegível para o envio."}</p>
                            </div>
                          ))}
                          {(scopeEligibleCompanies.length === 0 && scopedCompanies.length === 0) ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm leading-6 text-slate-600">
                              Nenhuma empresa elegível foi encontrada neste momento.
                            </div>
                          ) : null}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {scopeCompaniesMissingIntegration.length > 0 ? (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      Algumas empresas do seu escopo ainda precisam de configuração antes do envio. Mantenha na planilha apenas empresas prontas para uso.
                    </div>
                  ) : null}
                </StepCard>
              )}
            </div>
          ) : null}

          {currentStep === 2 ? (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={() => setCurrentStep(3)}
                  disabled={!(activeTemplate && hasEligibleContract && (activeContextCompanyId || isAdminSelectableMultiCompanyJourney))}
                  className="bg-[#002F59] hover:bg-[#002F59]/90"
                >
                  Já preenchi a planilha
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              <StepCard
                title={isSimpleOnlyExperience ? "Sua empresa já está definida" : "Revise as informações deste envio"}
                description={
                  isSimpleOnlyExperience
                    ? `Você está enviando dados para ${destinationLabel}. Nesta etapa basta baixar a planilha correta.`
                    : selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
                      ? "Você escolheu o envio para mais de uma empresa. Confira abaixo quais empresas fazem parte deste envio e quais planos podem ser usados em cada uma delas."
                      : `Você está preparando o envio para ${destinationLabel}. Agora baixe a planilha e use o modelo correspondente.`
                }
              >
                <div className="space-y-4">
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-950">
                        {selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY ? "Empresas deste envio" : "Empresa de destino"}
                      </p>
                      {selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY ? (
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          Esta planilha pode trazer <strong className="text-slate-950">{companiesInCurrentScope.length} empresa(s)</strong> do seu escopo.
                        </p>
                      ) : (
                        <>
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            Você está enviando dados para <strong className="text-slate-950">{destinationLabel}</strong>.
                          </p>
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            <strong className="text-slate-950">Planos disponíveis:</strong> {selectedCompanyPlansSummary}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-950">Modelo escolhido</p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{friendlyTemplateLabel}</p>
                    </div>
                  </div>

                  {selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY ? (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                      <div className="mb-4 flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                          <Layers3 className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-950">Empresas e planos deste envio</p>
                          <p className="mt-1 text-sm leading-6 text-slate-600">
                            Antes de baixar a planilha, confira todas as empresas liberadas no seu escopo e os respectivos planos de cada uma.
                          </p>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-2xl border border-slate-200">
                        <div className="grid gap-px bg-slate-200 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                          <div className="bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-950">Empresa</div>
                          <div className="bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-950">Planos liberados</div>
                          {companiesInCurrentScope.length > 0 ? companiesInCurrentScope.map((scopeItem) => {
                            const companyRecord = resolveCompanyRecord(scopeItem);
                            const companyPlans = Array.isArray(scopeItem?.activePlans)
                              ? scopeItem.activePlans
                              : Array.isArray(companyRecord?.activePlans)
                                ? companyRecord.activePlans
                                : [];
                            const companyKey = companyRecord?.id ?? getCompanyDisplayName(scopeItem);

                            return (
                              <div key={`scope-company-${companyKey}`} className="contents">
                                <div className="bg-white px-4 py-4 text-sm text-slate-700">
                                  <p className="font-semibold text-slate-950">{getCompanyDisplayName(scopeItem)}</p>
                                  <p className="mt-1 leading-6 text-slate-600">{getCompanyDescription(scopeItem) || "Empresa liberada para este envio."}</p>
                                </div>
                                <div className="bg-white px-4 py-4 text-sm leading-6 text-slate-600">
                                  {companyPlans.length > 0 ? (
                                    <div className="grid gap-2">
                                      {companyPlans.map((plan: any, index: number) => (
                                        <div key={`${companyKey}-plan-${index}-${getPlanDisplayName(plan)}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-semibold text-slate-950">{getPlanDisplayName(plan)}</p>
                                            <span className="rounded-full bg-blue-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-700">
                                              {getPlanRelationshipLabel(plan)}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-xs text-slate-500">{getPlanDescription(plan) || "Sem descrição local cadastrada."}</p>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    getPlanSummary(companyPlans)
                                  )}
                                </div>
                              </div>
                            );
                          }) : (
                            <div className="bg-white px-4 py-4 text-sm leading-6 text-slate-600 md:col-span-2">
                              Nenhuma empresa elegível foi encontrada neste momento.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-950">Resumo da empresa escolhida</p>
                      <div className="mt-3 grid gap-4 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Empresa</p>
                          <p className="mt-2 text-sm font-semibold text-slate-950">{selectedOperationalCompany ? getCompanyDisplayName(selectedOperationalCompany) : destinationLabel}</p>
                          <p className="mt-1 text-sm leading-6 text-slate-600">{selectedOperationalCompany ? getCompanyDescription(selectedOperationalCompany) || "Empresa definida para este envio." : "Empresa definida para este envio."}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Planos disponíveis</p>
                          {selectedOperationalCompany?.activePlans?.length ? (
                            <div className="mt-3 grid gap-2">
                              {selectedOperationalCompany.activePlans.map((plan: any, index: number) => (
                                <div key={`selected-company-plan-${index}-${getPlanDisplayName(plan)}`} className="rounded-2xl border border-slate-200 bg-white p-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold leading-6 text-slate-950">{getPlanDisplayName(plan)}</p>
                                    <span className="rounded-full bg-blue-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-700">
                                      {getPlanRelationshipLabel(plan)}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-xs leading-5 text-slate-500">{getPlanDescription(plan) || "Sem descrição local cadastrada."}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm font-semibold leading-6 text-slate-950">{selectedCompanyPlansSummary}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </StepCard>

              <StepCard
                title="Baixe a planilha correta"
                description="Use sempre o modelo oficial. Isso ajuda a evitar erros na conferência."
                tone="soft"
              >
                <div className="space-y-4">
                  <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                          <FileSpreadsheet className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-base font-semibold text-slate-950">{friendlyTemplateLabel}</p>
                          <p className="mt-1 text-sm leading-6 text-slate-600">
                            {selectedTemplateType === LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY
                              ? "Este modelo permite informar empresas diferentes na mesma planilha."
                              : "Este modelo é indicado quando o envio será para uma única empresa."}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleDownloadDictionary}
                        >
                          <FileCheck2 className="mr-2 h-4 w-4" />
                          Baixar dicionário
                        </Button>
                        <Button
                          type="button"
                          onClick={() => onDownloadWorkbook("template", selectedTemplateType)}
                          className="bg-[#002F59] hover:bg-[#002F59]/90"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Baixar planilha
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="planilha-dictionary" className="rounded-2xl border border-slate-200 bg-white px-5">
                      <AccordionTrigger className="text-sm font-semibold text-slate-950 hover:no-underline">
                        Ver dicionário deste modelo
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 pt-2">
                        <div className="space-y-3">
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                            Este dicionário acompanha o modelo mostrado acima. Se você mudar o tipo de planilha, o conteúdo abaixo muda junto.
                          </div>
                          <div className="grid gap-3">
                            {importDictionaryEntries.map((entry) => (
                              <div key={entry.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-semibold text-slate-950">{entry.label}</p>
                                  <Badge variant={entry.required ? "default" : "secondary"}>
                                    {entry.required ? "Obrigatório" : "Opcional"}
                                  </Badge>
                                </div>
                                <p className="mt-3"><strong className="text-slate-950">Como preencher:</strong> {entry.fill}</p>
                                <p className="mt-2"><strong className="text-slate-950">O que é permitido:</strong> {entry.allowed}</p>
                                <p className="mt-2"><strong className="text-slate-950">Exemplo:</strong> {entry.example}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="planilha-rules" className="rounded-2xl border border-slate-200 bg-white px-5">
                      <AccordionTrigger className="text-sm font-semibold text-slate-950 hover:no-underline">
                        Ver regras da planilha
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 pt-2">
                        <div className="space-y-3 text-sm leading-6 text-slate-600">
                          {modelRules.map((rule) => (
                            <div key={rule} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                              {rule}
                            </div>
                          ))}
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <p className="font-semibold text-slate-950">Campos principais do modelo</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {visibleTemplateFields.slice(0, 10).map((field) => (
                                <Badge key={field.key || field.name || field.label} variant="secondary">
                                  {field.label || field.name || field.key}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {!hasEligibleContract ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      Não encontramos contrato elegível para o contexto atual. Ajuste a configuração antes de seguir com o envio.
                    </div>
                  ) : null}
                </div>
              </StepCard>
            </div>
          ) : null}

          {currentStep === 3 ? (
            <div className="space-y-6">
              <StepCard
                title="Envie a planilha para conferência"
                description="Selecione o arquivo preenchido. Nesta etapa o sistema vai conferir o modelo e os dados antes do envio."
              >
                <ImportUploadZone
                  onFileSelected={(file) => setSelectedImportFile(file)}
                  onUpload={onPreviewUpload}
                  isLoading={previewImportPending}
                  error={friendlyPreviewError ?? undefined}
                  acceptedFileExtensions={[".xlsx", ".xlsm", ".csv"]}
                  acceptedTypesHint="Formatos aceitos: .xlsx, .xlsm ou .csv com até 5 MB."
                  success={
                    previewResult && selectedImportFile
                      ? `Encontramos ${previewResult.validRowsCount} pessoa(s) pronta(s) para envio e ${previewResult.invalidRowsCount} item(ns) para corrigir.`
                      : undefined
                  }
                  disabled={(!activeContextCompanyId && !isAdminSelectableMultiCompanyJourney) || !hasEligibleContract}
                  title="Envie sua planilha preenchida"
                  description="Escolha o arquivo no seu computador para iniciar a conferência."
                  submitButtonLabel="Conferir planilha"
                  clearButtonLabel="Enviar outra planilha"
                  selectedFileLabel="Arquivo selecionado"
                  successTitle="Sua planilha foi conferida"
                  errorTitle="Não conseguimos conferir esta planilha"
                />
              </StepCard>

              {selectedImportFile ? (
                <StepCard title="Arquivo selecionado" tone="soft">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Nome do arquivo</p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">{selectedImportFile.name}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Empresa de destino</p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">{destinationLabel}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Modelo usado</p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">{friendlyTemplateLabel}</p>
                    </div>
                  </div>
                </StepCard>
              ) : null}

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="quick-tips" className="rounded-[1.5rem] border border-slate-200 bg-white px-5 shadow-sm">
                  <AccordionTrigger className="text-sm font-semibold text-slate-950 hover:no-underline">
                    Ver dicas rápidas antes de conferir
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pt-2">
                    <div className="grid gap-3 md:grid-cols-3">
                      {[
                        "Use o modelo correto para o tipo de envio escolhido.",
                        "Confira os campos obrigatórios antes de enviar.",
                        "Revise se a ação da pessoa está correta na planilha.",
                      ].map((tip) => (
                        <div key={tip} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                          {tip}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {previewSummary ? (
                <StepCard
                  title={previewSummary.adjustment > 0 ? "Encontramos itens que precisam de correção" : "Sua planilha foi conferida"}
                  description={
                    previewSummary.adjustment > 0
                      ? "Algumas linhas da planilha precisam de ajuste antes do envio completo."
                      : `Encontramos ${previewSummary.ready} pessoa(s) pronta(s) para envio.`
                  }
                >
                  <div className="grid gap-4 md:grid-cols-3">
                    <SummaryMetric label="Total de pessoas encontradas" value={previewSummary.total} />
                    <SummaryMetric label="Prontas para envio" value={previewSummary.ready} tone="positive" />
                    <SummaryMetric label="Itens para corrigir" value={previewSummary.adjustment} tone="warning" />
                  </div>

                  {previewTopIssues.length > 0 ? (
                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-950">O que merece atenção</p>
                      <div className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                        {previewTopIssues.map((issue) => (
                          <div key={issue.label} className="rounded-2xl border border-white bg-white px-4 py-3">
                            <p className="font-medium text-slate-950">{sanitizeVisibleCopy(issue.label)}</p>
                            {issue.rowNumbers.length > 0 ? (
                              <p className="mt-1 text-sm text-slate-600">
                                {issue.rowNumbers.length === 1
                                  ? `Linha afetada: ${issue.rowNumbers[0]}`
                                  : `Linhas afetadas: ${issue.rowNumbers.join(", ")}`}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {previewSummary.adjustment > 0 && previewSummary.ready > 0 ? (
                    <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
                      As pessoas prontas podem ser enviadas agora. Os itens com correção poderão ser importados depois.
                    </div>
                  ) : null}

                  {previewSummary.adjustment > 0 && previewSummary.ready === 0 ? (
                    <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      Corrija os itens indicados e envie a planilha novamente.
                    </div>
                  ) : null}

                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button
                      onClick={onCreateDraft}
                      disabled={createDraftPending || !selectedImportFile || previewSummary.ready === 0}
                      className="bg-[#002F59] hover:bg-[#002F59]/90"
                    >
                      {createDraftPending ? "Gerando resumo do envio..." : "Gerar resumo do envio"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    {previewSummary.adjustment > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => downloadCorrectionsReport(previewResult, destinationLabel, friendlyTemplateLabel)}
                      >
                        Baixar relatório de correções
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSelectedImportFile(null)}
                    >
                      Enviar outra planilha
                    </Button>
                    {selectedDraft?.id ? (
                      <Button variant="outline" onClick={() => setCurrentStep(4)}>
                        Ir para conferir e concluir
                      </Button>
                    ) : null}
                  </div>
                </StepCard>
              ) : null}
            </div>
          ) : null}

          {currentStep === 4 ? (
            <div className="space-y-6">
              {finalJourneyState === "sent" ? (
                <StepCard
                  title="Envio concluído com sucesso"
                  description="Recebemos sua planilha e vamos iniciar o processamento dos dados. Você poderá acompanhar o andamento no histórico de importações."
                >
                  <div className="grid gap-4 md:grid-cols-4">
                    <SummaryMetric label="Pessoas encontradas" value={selectedDraft?.totalRows ?? previewResult?.totalRows ?? 0} />
                    <SummaryMetric label="Prontas para envio" value={selectedDraft?.approvedRows ?? previewResult?.validRowsCount ?? 0} tone="positive" />
                    <SummaryMetric label="Itens para corrigir" value={selectedDraft?.rejectedRows ?? previewResult?.invalidRowsCount ?? 0} tone="warning" />
                    <SummaryMetric label="Situação do envio" value={operationalStatusLabel} />
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button
                      className="bg-[#002F59] hover:bg-[#002F59]/90"
                      onClick={() => setLocation("/portal/base-de-pessoas/historico")}
                    >
                      Ver histórico de importações
                    </Button>
                    <Button variant="outline" onClick={handleStartNewSend}>
                      Fazer novo envio
                    </Button>
                  </div>
                </StepCard>
              ) : (
                <StepCard
                    title={
                      finalJourneyState === "error"
                        ? "Seu envio precisa de atenção"
                        : finalJourneyState === "sending"
                          ? "Envio iniciado com sucesso"
                          : finalJourneyState === "stuck"
                            ? "Lote aguardando reenvio"
                            : finalJourneyState === "preparing"
                            ? "Preparando envio"
                            : finalJourneyState === "prepare"
                              ? "Tudo pronto para revisar"
                              : "Tudo pronto para enviar"
                    }
                    description={
                      finalJourneyState === "error"
                        ? "Encontramos um impedimento neste envio. Revise a mensagem abaixo antes de tentar novamente."
                        : finalJourneyState === "sending"
                          ? "Sua parte foi concluída. Agora o sistema continuará o processamento automaticamente."
                          : finalJourneyState === "stuck"
                            ? "Detectamos que o lote ficou preso em validação sem evidência de envio externo. Reenvie o lote para retomar o processamento."
                            : finalJourneyState === "preparing"
                            ? "Estamos organizando o envio para a etapa final."
                            : finalJourneyState === "prepare"
                              ? "Revise o resumo abaixo antes de gerar o lote final para envio."
                              : "As pessoas prontas podem ser enviadas agora. Os itens com correção poderão ser importados depois."
                    }

                >
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <SummaryMetric label="Empresa de destino" value={destinationLabel} />
                    <SummaryMetric
                      label="Arquivo enviado"
                      value={selectedDraft?.fileName || selectedImportFile?.name || "Não informado"}
                      valueClassName="text-base leading-snug break-words md:text-xl xl:text-2xl"
                    />
                    <SummaryMetric label="Pessoas prontas para envio" value={selectedDraft?.approvedRows ?? previewResult?.validRowsCount ?? 0} tone="positive" />
                    <SummaryMetric label="Itens para corrigir" value={selectedDraft?.rejectedRows ?? previewResult?.invalidRowsCount ?? 0} tone="warning" />
                  </div>

                  <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                    <p><strong className="text-slate-950">Modelo usado:</strong> {friendlyTemplateLabel}</p>
                    <p className="mt-2"><strong className="text-slate-950">Situação do envio:</strong> {operationalStatusLabel}</p>
                    {(selectedDraft?.rejectedRows ?? previewResult?.invalidRowsCount ?? 0) > 0 ? (
                      <p className="mt-2">As pessoas prontas podem ser enviadas agora. Os itens com correção poderão ser importados depois.</p>
                    ) : null}
                  </div>

                  {selectedDraft?.isDiscarded ? (
                    <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                      Este envio foi encerrado antes da conclusão e não pode mais ser confirmado ou processado.
                    </div>
                  ) : null}

                  <div className="mt-5 flex flex-wrap gap-3">
                    {finalJourneyState === "preparing" ? (
                      <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-900">
                        Preparando envio...
                      </div>
                    ) : null}

                    {shouldShowSendAction ? (
                      <Button
                        onClick={() => onProcessDraft(selectedDraft.id)}
                        className="bg-[#002F59] hover:bg-[#002F59]/90"
                      >
                        Concluir envio
                      </Button>
                    ) : null}

                    {shouldShowRetryStuckAction ? (
                      <Button
                        onClick={() => onRetryStuckDraft(selectedDraft.id)}
                        disabled={retryStuckDraftPending}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <RefreshCcw className="mr-2 h-4 w-4" />
                        Reenviar lote preso
                      </Button>
                    ) : null}

                    {shouldShowRefreshAction ? (
                      <Button
                        variant="outline"
                        onClick={() => onRefreshDraftStatus(selectedDraft.id)}
                        disabled={refreshDraftPending || !selectedOperationalTracking?.hasTracking}
                      >
                        <RefreshCcw className="mr-2 h-4 w-4" />
                        Acompanhar processamento
                      </Button>
                    ) : null}

                    {(["sending", "sent"] as const).includes(finalJourneyState as "sending" | "sent") ? (
                      <Button variant="outline" onClick={handleStartNewSend}>
                        Fazer nova importação
                      </Button>
                    ) : null}

                    {selectedDraft?.canDiscard ? (
                      <Button
                        variant="outline"
                        className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                        onClick={() => onDiscardDraft(selectedDraft.id)}
                        disabled={discardDraftPending || finalJourneyState === "sending"}
                      >
                        Voltar e ajustar
                      </Button>
                    ) : null}
                  </div>
                </StepCard>
              )}

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="details" className="rounded-[1.5rem] border border-slate-200 bg-white px-5 shadow-sm">
                  <AccordionTrigger className="text-sm font-semibold text-slate-950 hover:no-underline">
                    Ver detalhes do envio
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pt-2">
                    <div className="space-y-6">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-sm font-semibold text-slate-950">Situação do envio</p>
                          {selectedOperationalTracking ? (
                            <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-medium text-slate-950">{sanitizeVisibleCopy(selectedOperationalTracking.uiStatus)}</p>
                                <Badge variant={getOperationalStatusTone(selectedOperationalTracking.uiStatus)}>
                                  {sanitizeVisibleCopy(selectedOperationalTracking.uiStatus)}
                                </Badge>
                              </div>
                              <p>{sanitizeVisibleCopy(selectedOperationalTracking.message)}</p>
                              {selectedOperationalTracking.hasConfirmedExternalReceipt ? (
                                <div className="rounded-2xl border border-white bg-white p-4 text-xs leading-5 text-slate-600">
                                  <p><strong className="text-slate-950">Upload ID:</strong> {selectedOperationalTracking.uploadId || "Não informado"}</p>
                                  <p className="mt-2"><strong className="text-slate-950">Processing code:</strong> {selectedOperationalTracking.processingCode || "Não informado"}</p>
                                  <p className="mt-2"><strong>S3 Key:</strong> {selectedOperationalTracking.s3Key || "Aguardando retorno da plataforma externa"}</p>
                                </div>
                              ) : selectedOperationalTracking.hasTracking ? (
                                <p className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                                  Os identificadores externos definitivos só serão exibidos depois que a I4H confirmar o recebimento do upload.
                                </p>
                              ) : null}
                            </div>
                          ) : (
                            <p className="mt-3 text-sm leading-6 text-slate-600">Ainda não há acompanhamento detalhado disponível para este envio.</p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-sm font-semibold text-slate-950">Resumo técnico recolhido</p>
                          <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                            <div className="rounded-2xl border border-white bg-white p-4">
                              <p><strong className="text-slate-950">ID do envio:</strong> {selectedDraft?.id ? `#${selectedDraft.id}` : draftResult?.jobId ? `#${draftResult.jobId}` : "Aguardando geração"}</p>
                              <p className="mt-2"><strong className="text-slate-950">Arquivo:</strong> {selectedDraft?.fileName || selectedImportFile?.name || "Não informado"}</p>
                              <p className="mt-2"><strong className="text-slate-950">Última atualização:</strong> {formatDateTime(selectedOperationalTracking?.updatedAt || selectedDraft?.updatedAt || selectedDraft?.createdAt)}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-4 xl:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-sm font-semibold text-slate-950">Amostra de pessoas conferidas</p>
                          <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                            {draftRows.slice(0, 5).map((row: any) => {
                              const details = getInspectedLifeBaseRowDetails(row);
                              return (
                                <div key={row.id} className="rounded-2xl border border-white bg-white p-4">
                                  <p className="font-medium text-slate-950">Linha {row.rowNumber}</p>
                                  <p className="mt-1">{details.name || "Nome não informado"}{details.cpf ? ` · CPF ${details.cpf}` : ""}</p>
                                  <p className="mt-1">{details.plan ? `Plano: ${details.plan}` : "Plano não informado"}</p>
                                  <p className="mt-1">{details.relationship ? `Tipo: ${getLifeBaseRelationshipLabel(details.relationship) || details.relationship}` : "Tipo não informado"}</p>
                                </div>
                              );
                            })}
                            {draftRows.length === 0 ? (
                              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4">
                                Nenhuma linha conferida está disponível para visualização neste momento.
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-sm font-semibold text-slate-950">Eventos recentes</p>
                          <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                            {draftEvents.slice(0, 6).map((event: any, index: number) => (
                              <div key={`${event.createdAt}-${index}`} className="rounded-2xl border border-white bg-white p-4">
                                <p className="font-medium text-slate-950">{sanitizeVisibleCopy(event.message || event.statusLabel || event.status || "Atualização do envio")}</p>
                                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-400">{formatDateTime(event.createdAt || event.timestamp)}</p>
                              </div>
                            ))}
                            {draftEvents.length === 0 ? (
                              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4">
                                Nenhum evento recente foi registrado para este envio.
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(previousVisibleStepId)}
              disabled={currentStep === firstVisibleStepId}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {currentStep === 4 ? "Voltar e ajustar" : "Voltar"}
            </Button>

            <div className="flex flex-wrap gap-3">
              {currentStep === 1 ? (
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={!stepOneReady}
                  className="bg-[#002F59] hover:bg-[#002F59]/90"
                >
                  Continuar
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : null}

              {currentStep === 2 ? (
                <Button
                  onClick={() => setCurrentStep(3)}
                  disabled={!(activeTemplate && hasEligibleContract && (activeContextCompanyId || isAdminSelectableMultiCompanyJourney))}
                  className="bg-[#002F59] hover:bg-[#002F59]/90"
                >
                  Já preenchi a planilha
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : null}

              {currentStep === 3 && selectedDraft?.id ? (
                <Button onClick={() => setCurrentStep(4)} className="bg-[#002F59] hover:bg-[#002F59]/90">
                  Ir para conferir e concluir
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
